import { BookmarkablePage, DetailView, DetailViewsPanel, GroupItem, useNavigation } from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import * as Messages from "../../codegen/Messages";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import {
  DetailsPanelId,
  EXADATA_LIST_HUB_ROUTE,
  PageId,
  PageRegistrationConfig,
} from "../../constants/pluginConstants";
import { ExaDbList } from "../ExaDbListPage/ExaDbList";
import { ExaInfraList } from "../ExaInfraListPage/ExaInfraList";
import { ExaVmClusterList } from "../ExaVmClusterListPage/ExaVmClusterList";

const registrationIds = PageRegistrationConfig[PageId.EXA_LIST_HUB].map(config => config.key);

const getViewId = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.INFRASTRUCTURES:
      return registrationIds[2];
    case DetailsPanelId.VMCLUSTERS:
      return registrationIds[1];
    case DetailsPanelId.DATABASES:
    default:
      return registrationIds[0];
  }
};

const getIconName = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.INFRASTRUCTURES:
      return "infra-svg";
    case DetailsPanelId.VMCLUSTERS:
      return "vmcluster-svg";
    case DetailsPanelId.DATABASES:
    default:
      return "exa-svg";
  }
};

export const ExaListHubPage = (): JSX.Element => {
  const { listId } = useParams();
  const filterState = React.useContext<FilterState>(FilterStateContext);

  const { back, navigateToSelf, navigateToPanel } = useNavigation(ConsoleContext);

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    const registrationConfig = PageRegistrationConfig[PageId.EXA_LIST_HUB].find(
      config => config.panelPath === listId,
    );
    const path = `${EXADATA_LIST_HUB_ROUTE}/${listId}`;
    navigateToSelf(path, registrationConfig?.key || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (filterState.pillFilterStateValue.listId !== listId) {
      filterState.setFilterStateValue({ filterValues: undefined, listId: undefined, defaultPills: undefined });
      filterState.setFilterTextValue(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMenuItemSelect = (id: string): void => {
    filterState.setFilterStateValue({ filterValues: undefined, listId: undefined, defaultPills: undefined });
    filterState.setFilterTextValue(undefined);
    navigateToPanel(id);
  };

  const detailViews: DetailView[] = [
    {
      id: registrationIds[0],
      content: (<ExaDbList />),
    },
    {
      id: registrationIds[1],
      content: (<ExaVmClusterList />),
    },
    {
      id: registrationIds[2],
      content: (<ExaInfraList />),
    },
  ];

  const groupItems: GroupItem[] = [
    {
      heading: Messages.labels.dedicated(),
      items: [
        { icon: getIconName(DetailsPanelId.DATABASES), id: detailViews[0].id, name: Messages.labels.databases() },
        { icon: getIconName(DetailsPanelId.VMCLUSTERS), id: detailViews[1].id, name: Messages.labels.vmClusters() },
        { icon: getIconName(DetailsPanelId.INFRASTRUCTURES), id: detailViews[2].id, name: Messages.labels.infras() },
      ],
    },
  ];

  const getTitleSuffix = (panelId: string | undefined): string => {
    switch (panelId) {
      case DetailsPanelId.INFRASTRUCTURES:
        return Messages.labels.infras();
      case DetailsPanelId.VMCLUSTERS:
        return Messages.labels.vmClusters();
      case DetailsPanelId.DATABASES:
      default:
        return Messages.labels.databases();
    }
  };

  const getTitle = (
    panelId: string | undefined,
  ): string => `${Messages.serviceProviders.exadataDedicated()} | ${getTitleSuffix(panelId)}`;

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={Messages.serviceProviders.exadataDedicated()}
      titleSuffix={getTitleSuffix(listId)}
    >
      <DetailViewsPanel
        anchorId="page"
        title={getTitle(listId)}
        icon={getIconName(listId)}
        onClose={back}
        isOpen
        views={detailViews}
        activeViewId={getViewId(listId)}
        onMenuItemSelect={onMenuItemSelect}
        menu={groupItems}
      />
    </BookmarkablePage>
  );
};
